import {FC, ReactNode} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {RootState} from 'setup'
import {hideSampleGenerationLimitPopup} from 'setup/redux/Actions/SampleReadOnly/sampleReadOnly'

type SampleNoticeProps = {
  /** i18n id of the title. */
  titleId: string
  /** i18n id of the message; a <b>…</b> part (the button to use) is emphasised. */
  messageId: string
  /** Font Awesome icon, e.g. 'fa-file-excel'. */
  icon: string
  /** Red "blocked" icon: the user cannot go on this way. */
  isBlocking?: boolean
  /** On a page (icon beside the text) instead of a centred dialog. */
  inline?: boolean
  /** Shows the close cross and the primary "Understood" button. */
  onClose?: () => void
}

/**
 * A da1timetable sample notice in the "Final Check Before Generation" pre-validation design
 * (`ttwiz ttfc` tokens): one icon, a title, the message with the button to use emphasised and,
 * when it can be closed, a single primary action. Styles: _sampleNotice.scss.
 */
export const SampleNoticePanel: FC<SampleNoticeProps> = ({
  titleId,
  messageId,
  icon,
  isBlocking = false,
  inline = false,
  onClose,
}) => {
  const intl = useIntl()
  const bold = (chunks: ReactNode) => <b>{chunks}</b>

  return (
    <div className={`sample-notice ${inline ? 'sample-notice--inline' : ''}`}>
      {onClose && (
        <button
          type='button'
          className='ttfc-close sample-notice__close'
          onClick={onClose}
          aria-label={intl.formatMessage({id: 'GENERAL.CLOSE'})}
        >
          <i className='fa-solid fa-xmark' aria-hidden='true'></i>
        </button>
      )}

      <span className={`sample-notice__icon ${isBlocking ? 'is-danger' : ''}`}>
        <i className={`fa-solid ${icon}`} aria-hidden='true'></i>
      </span>

      <div>
        <h4 className='sample-notice__title'>{intl.formatMessage({id: titleId})}</h4>
        <p className='sample-notice__message'>{intl.formatMessage({id: messageId}, {b: bold})}</p>

        {onClose && (
          <div className='sample-notice__actions'>
            <button
              type='button'
              className='ttfc-generate-btn sample-notice__action'
              onClick={onClose}
            >
              {intl.formatMessage({id: 'GENERAL.UNDERSTOOD'})}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * "You've reached the sample timetable generation limit": the free sample generations of
 * the da1timetable sample workspace are used up. Opened before the generation queue
 * (useTimetableGeneration) and, as a fallback, on the API's SAMPLE_GENERATION_LIMIT refusal
 * (StatusQueue). Mounted once, in MasterLayout.
 */
const SampleGenerationLimitModal: FC = () => {
  const dispatch = useDispatch()
  const show = useSelector<RootState>(
    ({sampleReadOnlyReducer}) => sampleReadOnlyReducer.showLimitPopup
  ) as boolean

  const close = () => {
    dispatch(hideSampleGenerationLimitPopup())
  }

  return (
    <Modal
      show={show}
      onHide={close}
      centered
      contentClassName='ttwiz ttfc'
      dialogClassName='sample-notice-dialog'
    >
      <SampleNoticePanel
        titleId='SAMPLE_DATA.GENERATION_LIMIT.TITLE'
        messageId='SAMPLE_DATA.GENERATION_LIMIT.MESSAGE'
        icon='fa-ban'
        isBlocking
        onClose={close}
      />
    </Modal>
  )
}

export default SampleGenerationLimitModal
